/**
 * Progress Trends Chart - Widget 3
 * 
 * Shows daily progress trends with line charts for accuracy, speed, and WPM
 * Includes session counts and quality indicators
 */

import React, { useState, useMemo } from 'react';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { DailyProgressData, TimePeriod } from '@/types/statistics';

interface ProgressTrendsChartProps {
  data: DailyProgressData[];
  loading: boolean;
  period: TimePeriod;
}

type ChartMetric = 'accuracy' | 'speed' | 'wpm' | 'sessions' | 'quality';
type ChartType = 'line' | 'area';

const ProgressTrendsChart: React.FC<ProgressTrendsChartProps> = ({ 
  data, 
  loading, 
  period 
}) => {
  const [selectedMetric, setSelectedMetric] = useState<ChartMetric>('accuracy');
  const [chartType, setChartType] = useState<ChartType>('line');
  const [showGoalLine, setShowGoalLine] = useState(true);

  // Process data for chart display
  const processedData = useMemo(() => {
    return data.map(day => ({
      ...day,
      dateFormatted: formatDate(day.date),
      shortDate: formatShortDate(day.date),
      qualityColor: getQualityColor(day.averageSessionQuality),
      dayOfWeek: new Date(day.date).toLocaleDateString('en', { weekday: 'short' })
    }));
  }, [data]);

  // Calculate trends and statistics
  const trendAnalysis = useMemo(() => {
    if (data.length < 2) return null;

    const calculateTrend = (metric: ChartMetric) => {
      const values = data.map(day => {
        switch (metric) {
          case 'accuracy': return day.accuracy;
          case 'speed': return day.speed;
          case 'wpm': return day.wpm;
          case 'sessions': return day.sessionsCount;
          case 'quality': return day.averageSessionQuality;
          default: return 0;
        }
      });

      const firstHalf = values.slice(0, Math.floor(values.length / 2));
      const secondHalf = values.slice(Math.floor(values.length / 2));
      
      const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
      const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
      
      const improvement = ((secondAvg - firstAvg) / firstAvg) * 100;
      
      return {
        improvement,
        direction: improvement > 2 ? 'up' : improvement < -2 ? 'down' : 'stable',
        firstAvg,
        secondAvg
      };
    };

    return {
      accuracy: calculateTrend('accuracy'),
      speed: calculateTrend('speed'),
      wpm: calculateTrend('wpm'),
      sessions: calculateTrend('sessions'),
      quality: calculateTrend('quality')
    };
  }, [data]);

  // const _getGoalValue = (metric: ChartMetric): number => {
  //   switch (metric) {
  //     case 'accuracy': return 95;
  //     case 'speed': return 3;
  //     case 'wpm': return 40;
  //     case 'sessions': return 3;
  //     case 'quality': return 8;
  //     default: return 0;
  //   }
  // };

  const getMetricInfo = (metric: ChartMetric) => {
    const configs = {
      accuracy: { 
        label: 'Accuracy (%)', 
        color: '#10b981', 
        unit: '%',
        goal: 95,
        icon: '🎯'
      },
      speed: { 
        label: 'Speed (letters/sec)', 
        color: '#3b82f6', 
        unit: ' l/s',
        goal: 3,
        icon: '⚡'
      },
      wpm: { 
        label: 'Words per Minute', 
        color: '#8b5cf6', 
        unit: ' WPM',
        goal: 40,
        icon: '📝'
      },
      sessions: { 
        label: 'Sessions per Day', 
        color: '#f59e0b', 
        unit: '',
        goal: 3,
        icon: '📅'
      },
      quality: { 
        label: 'Session Quality (1-10)', 
        color: '#ef4444', 
        unit: '',
        goal: 8,
        icon: '⭐'
      }
    };
    return configs[metric];
  };

  if (loading) {
    return <ProgressTrendsSkeletonBlock />;
  }

  if (data.length === 0) {
    return <EmptyProgressTrendsBlock period={period} />;
  }

  const metricInfo = getMetricInfo(selectedMetric);

  return (
    <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
            📈 Progress Trends
          </h2>
          <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px', margin: 0 }}>
            Daily performance over time • {data.length} days tracked
          </p>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Chart Type Toggle */}
          <div style={{ display: 'flex', background: '#334155', borderRadius: '6px', padding: '2px' }}>
            <button
              onClick={() => setChartType('line')}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer',
                background: chartType === 'line' ? '#475569' : 'transparent',
                color: '#e2e8f0'
              }}
            >
              📈 Line
            </button>
            <button
              onClick={() => setChartType('area')}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer',
                background: chartType === 'area' ? '#475569' : 'transparent',
                color: '#e2e8f0'
              }}
            >
              📊 Area
            </button>
          </div>

          {/* Goal Line Toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#e2e8f0' }}>
            <input
              type="checkbox"
              checked={showGoalLine}
              onChange={(e) => setShowGoalLine(e.target.checked)}
              style={{ borderRadius: '3px' }}
            />
            <span>Show Goal</span>
          </label>
        </div>
      </div>

      {/* Metric Selector */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
        {(['accuracy', 'speed', 'wpm', 'sessions', 'quality'] as ChartMetric[]).map(metric => {
          const config = getMetricInfo(metric);
          const trend = trendAnalysis?.[metric];
          
          return (
            <button
              key={metric}
              onClick={() => setSelectedMetric(metric)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 10px',
                borderRadius: '6px',
                fontWeight: '500',
                fontSize: '11px',
                border: 'none',
                cursor: 'pointer',
                background: selectedMetric === metric ? '#3b82f6' : '#334155',
                color: '#ffffff'
              }}
            >
              <span>{config.icon}</span>
              <span>{config.label}</span>
              {trend && (
                <span style={{
                  fontSize: '9px',
                  padding: '1px 4px',
                  borderRadius: '4px',
                  background: selectedMetric === metric 
                    ? 'rgba(255,255,255,0.2)' 
                    : trend.direction === 'up' 
                      ? '#166534'
                      : trend.direction === 'down'
                        ? '#dc2626'
                        : '#475569',
                  color: '#ffffff'
                }}>
                  {trend.direction === 'up' ? '↗' : trend.direction === 'down' ? '↘' : '→'}
                  {Math.abs(trend.improvement).toFixed(1)}%
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Chart */}
      <div style={{ height: '300px', marginBottom: '16px', background: '#334155', borderRadius: '6px', padding: '8px' }}>
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'area' ? (
            <AreaChart data={processedData}>
              <defs>
                <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={metricInfo.color} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={metricInfo.color} stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="shortDate"
                stroke="#6b7280"
                tick={{ fontSize: 12 }}
                tickMargin={8}
              />
              <YAxis 
                stroke="#6b7280" 
                tick={{ fontSize: 12 }}
                domain={selectedMetric === 'accuracy' ? [0, 100] : ['dataMin - 10', 'dataMax + 10']}
              />
              <Tooltip 
                formatter={(value: any) => [
                  `${value}${metricInfo.unit}`,
                  metricInfo.label
                ]}
                labelFormatter={(label: any, payload: any) => {
                  const data = payload?.[0]?.payload;
                  return data ? `${data.dateFormatted} (${data.dayOfWeek})` : label;
                }}
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              {showGoalLine && (
                <ReferenceLine 
                  y={metricInfo.goal} 
                  stroke="#9ca3af" 
                  strokeDasharray="5 5"
                  label={{ value: `Goal: ${metricInfo.goal}${metricInfo.unit}`, position: "insideTopRight" }}
                />
              )}
              <Area 
                type="monotone" 
                dataKey={selectedMetric}
                stroke={metricInfo.color}
                strokeWidth={2}
                fill="url(#colorGradient)"
                dot={{ fill: metricInfo.color, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: metricInfo.color, strokeWidth: 2 }}
              />
            </AreaChart>
          ) : (
            <LineChart data={processedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="shortDate"
                stroke="#6b7280"
                tick={{ fontSize: 12 }}
                tickMargin={8}
              />
              <YAxis 
                stroke="#6b7280" 
                tick={{ fontSize: 12 }}
                domain={selectedMetric === 'accuracy' ? [0, 100] : ['dataMin - 10', 'dataMax + 10']}
              />
              <Tooltip 
                formatter={(value: any) => [
                  `${value}${metricInfo.unit}`,
                  metricInfo.label
                ]}
                labelFormatter={(label: any, payload: any) => {
                  const data = payload?.[0]?.payload;
                  return data ? `${data.dateFormatted} (${data.dayOfWeek})` : label;
                }}
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              {showGoalLine && (
                <ReferenceLine 
                  y={metricInfo.goal} 
                  stroke="#9ca3af" 
                  strokeDasharray="5 5"
                  label={{ value: `Goal: ${metricInfo.goal}${metricInfo.unit}`, position: "insideTopRight" }}
                />
              )}
              <Line 
                type="monotone" 
                dataKey={selectedMetric}
                stroke={metricInfo.color}
                strokeWidth={3}
                dot={{ fill: metricInfo.color, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: metricInfo.color, strokeWidth: 2 }}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Trend Analysis */}
      {trendAnalysis && (
        <div style={{ borderTop: '1px solid #475569', paddingTop: '16px' }}>
          <TrendAnalysisSection 
            analysis={trendAnalysis} 
            selectedMetric={selectedMetric}
            data={data}
          />
        </div>
      )}
    </div>
  );
};

// Trend Analysis Section Component
const TrendAnalysisSection: React.FC<{
  analysis: any;
  selectedMetric: ChartMetric;
  data: DailyProgressData[];
}> = ({ analysis, selectedMetric, data }) => {

  const currentTrend = analysis[selectedMetric];
  const metricInfo = getMetricInfo(selectedMetric);

  // Calculate additional insights
  const totalDays = data.length;
  const activeDays = data.filter(day => day.sessionsCount > 0).length;
  const consistencyScore = Math.round((activeDays / totalDays) * 100);

  const bestDay = data.reduce((best, current) => {
    const currentValue = getCurrentMetricValue(current, selectedMetric);
    const bestValue = getCurrentMetricValue(best, selectedMetric);
    return currentValue > bestValue ? current : best;
  });

  const insights = generateInsights(analysis, data, consistencyScore);

  function getCurrentMetricValue(day: DailyProgressData, metric: ChartMetric): number {
    switch (metric) {
      case 'accuracy': return day.accuracy;
      case 'speed': return day.speed;
      case 'wpm': return day.wpm;
      case 'sessions': return day.sessionsCount;
      case 'quality': return day.averageSessionQuality;
      default: return 0;
    }
  }

  return (
    <div>
      <h3 style={{ fontSize: '11px', fontWeight: '500', color: '#e2e8f0', marginBottom: '12px' }}>Performance Analysis</h3>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
        {/* Trend Summary */}
        <div style={{ background: '#334155', borderRadius: '6px', padding: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px' }}>{metricInfo.icon}</span>
            <span style={{ fontWeight: '500', color: '#e2e8f0', fontSize: '12px' }}>{metricInfo.label}</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{
              fontSize: '18px',
              fontWeight: '700',
              color: currentTrend.direction === 'up' ? '#16a34a' :
                currentTrend.direction === 'down' ? '#dc2626' : '#94a3b8'
            }}>
              {currentTrend.direction === 'up' ? '📈' : 
               currentTrend.direction === 'down' ? '📉' : '📊'}
            </span>
            <div>
              <div style={{
                fontSize: '14px',
                fontWeight: '700',
                color: currentTrend.direction === 'up' ? '#16a34a' :
                  currentTrend.direction === 'down' ? '#dc2626' : '#94a3b8'
              }}>
                {currentTrend.improvement > 0 ? '+' : ''}{currentTrend.improvement.toFixed(1)}%
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8' }}>vs first half</div>
            </div>
          </div>
          
          <div style={{ fontSize: '11px', color: '#94a3b8' }}>
            {currentTrend.firstAvg.toFixed(1)}{metricInfo.unit} → {currentTrend.secondAvg.toFixed(1)}{metricInfo.unit}
          </div>
        </div>

        {/* Best Performance */}
        <div style={{ background: '#1e40af', borderRadius: '6px', padding: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px' }}>🏆</span>
            <span style={{ fontWeight: '500', color: '#dbeafe', fontSize: '12px' }}>Best Day</span>
          </div>
          
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#ffffff', marginBottom: '4px' }}>
            {getCurrentMetricValue(bestDay, selectedMetric).toFixed(1)}{metricInfo.unit}
          </div>
          
          <div style={{ fontSize: '11px', color: '#bfdbfe' }}>
            {formatDate(bestDay.date)}
          </div>
          
          <div style={{ fontSize: '10px', color: '#93c5fd', marginTop: '4px' }}>
            {bestDay.sessionsCount} sessions, {bestDay.totalKeystrokes} keystrokes
          </div>
        </div>

        {/* Consistency Score */}
        <div style={{ background: '#166534', borderRadius: '6px', padding: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px' }}>📅</span>
            <span style={{ fontWeight: '500', color: '#dcfce7', fontSize: '12px' }}>Consistency</span>
          </div>
          
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#ffffff', marginBottom: '4px' }}>
            {consistencyScore}%
          </div>
          
          <div style={{ fontSize: '11px', color: '#bbf7d0' }}>
            {activeDays} of {totalDays} days
          </div>
          
          <div style={{ fontSize: '10px', color: '#86efac', marginTop: '4px' }}>
            {consistencyScore >= 80 ? 'Excellent!' :
             consistencyScore >= 60 ? 'Good progress' :
             consistencyScore >= 40 ? 'Keep improving' : 'More practice needed'}
          </div>
        </div>
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <h4 style={{ fontSize: '11px', fontWeight: '500', color: '#e2e8f0', marginBottom: '8px' }}>💡 Insights</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {insights.map((insight, index) => (
              <div 
                key={index}
                style={{
                  padding: '8px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  border: '1px solid',
                  ...(insight.type === 'positive' ? { background: '#166534', color: '#dcfce7', borderColor: '#22c55e' } :
                    insight.type === 'warning' ? { background: '#92400e', color: '#fef3c7', borderColor: '#f59e0b' } :
                    { background: '#1e40af', color: '#dbeafe', borderColor: '#3b82f6' })
                }}
              >
                {insight.message}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Functions
function getMetricInfo(metric: ChartMetric) {
  const configs = {
    accuracy: { 
      label: 'Accuracy (%)', 
      color: '#10b981', 
      unit: '%',
      goal: 95,
      icon: '🎯'
    },
    speed: { 
      label: 'Speed (letters/sec)', 
      color: '#3b82f6', 
      unit: ' l/s',
      goal: 3,
      icon: '⚡'
    },
    wpm: { 
      label: 'Words per Minute', 
      color: '#8b5cf6', 
      unit: ' WPM',
      goal: 40,
      icon: '📝'
    },
    sessions: { 
      label: 'Sessions per Day', 
      color: '#f59e0b', 
      unit: '',
      goal: 3,
      icon: '📅'
    },
    quality: { 
      label: 'Session Quality (1-10)', 
      color: '#ef4444', 
      unit: '',
      goal: 8,
      icon: '⭐'
    }
  };
  return configs[metric];
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatShortDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric'
  });
}

function getQualityColor(quality: number): string {
  if (quality >= 8) return '#10b981';
  if (quality >= 6) return '#f59e0b';
  return '#ef4444';
}

function generateInsights(analysis: any, _data: DailyProgressData[], consistencyScore: number) {
  const insights: Array<{ message: string; type: 'positive' | 'warning' | 'info' }> = [];

  // Consistency insights
  if (consistencyScore >= 80) {
    insights.push({
      message: "🌟 Excellent practice consistency! Your regular practice is paying off.",
      type: 'positive'
    });
  } else if (consistencyScore < 50) {
    insights.push({
      message: "📅 Try to practice more regularly. Consistency is key to improvement.",
      type: 'warning'
    });
  }

  // Accuracy insights
  if (analysis.accuracy.improvement > 5) {
    insights.push({
      message: "🎯 Your accuracy is improving significantly! Keep focusing on precision.",
      type: 'positive'
    });
  } else if (analysis.accuracy.improvement < -5) {
    insights.push({
      message: "⚠️ Your accuracy has declined recently. Try slowing down to focus on precision.",
      type: 'warning'
    });
  }

  // Speed insights
  if (analysis.speed.improvement > 10) {
    insights.push({
      message: "⚡ Great speed improvement! You're typing much faster than before.",
      type: 'positive'
    });
  }

  // WPM insights
  if (analysis.wpm.secondAvg >= 40) {
    insights.push({
      message: "🚀 You've reached advanced typing speed! Consider challenging yourself with complex texts.",
      type: 'positive'
    });
  } else if (analysis.wpm.secondAvg >= 25) {
    insights.push({
      message: "📈 You're making good progress toward advanced typing speed.",
      type: 'info'
    });
  }

  return insights;
}

// Loading Skeleton
const ProgressTrendsSkeletonBlock: React.FC = () => (
  <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
      <div>
        <div style={{ height: '16px', background: '#475569', borderRadius: '4px', width: '180px', marginBottom: '8px' }}></div>
        <div style={{ height: '12px', background: '#475569', borderRadius: '4px', width: '200px' }}></div>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <div style={{ height: '32px', background: '#475569', borderRadius: '6px', width: '80px' }}></div>
        <div style={{ height: '32px', background: '#475569', borderRadius: '6px', width: '96px' }}></div>
      </div>
    </div>
    
    <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} style={{ height: '32px', background: '#475569', borderRadius: '6px', width: '120px' }}></div>
      ))}
    </div>
    
    <div style={{ height: '300px', background: '#334155', borderRadius: '6px', marginBottom: '16px' }}></div>
    
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} style={{ background: '#334155', borderRadius: '6px', padding: '12px' }}>
          <div style={{ height: '16px', background: '#475569', borderRadius: '4px', marginBottom: '8px' }}></div>
          <div style={{ height: '24px', background: '#475569', borderRadius: '4px', width: '60px', marginBottom: '4px' }}></div>
          <div style={{ height: '12px', background: '#475569', borderRadius: '4px', width: '96px' }}></div>
        </div>
      ))}
    </div>
  </div>
);

// Empty State
const EmptyProgressTrendsBlock: React.FC<{ period: TimePeriod }> = ({ period: _period }) => (
  <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
    <div style={{ textAlign: 'center', padding: '48px 0' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📈</div>
      <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#ffffff', marginBottom: '8px' }}>
        No Progress Data
      </h3>
      <p style={{ color: '#94a3b8', marginBottom: '16px', fontSize: '14px' }}>
        No daily progress data found for the selected time period.
      </p>
      <div style={{ fontSize: '12px', color: '#64748b' }}>
        Practice regularly to see your progress trends! 📊
      </div>
    </div>
  </div>
);

export default ProgressTrendsChart;